import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output
} from '@angular/core';

import { JobApplicationService } from '../../../../services/job-application';

@Component({
  selector: 'app-atw-cv-uploader',
  templateUrl: './atw-cv-uploader.component.html',
  styleUrl: './atw-cv-uploader.component.scss'
})
export class AtwCvUploaderComponent implements OnDestroy {
  @Input() jobId: any = null;
  @Output() applicationSubmitted = new EventEmitter<any>();

  form = {
    name: '',
    phone: '',
    email: ''
  };

  selectedFile: File | null = null;
  dragOver = false;

  isUploading = false;
  progress = 0;

  errorMessage = '';
  successMessage = '';

  private progressInterval: any = null;
  private readonly maxFileSize = 5 * 1024 * 1024;

  constructor(private jobApplicationService: JobApplicationService) {}

  ngOnDestroy(): void {
    this.clearProgress();
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file) {
      this.setFile(file);
    }

    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;

    const file = event.dataTransfer?.files?.[0];

    if (file) {
      this.setFile(file);
    }
  }

  setFile(file: File): void {
    this.errorMessage = '';
    this.successMessage = '';

    const fileName = file.name.toLowerCase();

    const isAllowed =
      file.type === 'application/pdf' ||
      file.type === 'application/msword' ||
      file.type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      fileName.endsWith('.pdf') ||
      fileName.endsWith('.doc') ||
      fileName.endsWith('.docx');

    if (!isAllowed) {
      this.selectedFile = null;
      this.errorMessage = 'Please upload PDF, DOC, or DOCX file only.';
      return;
    }

    if (file.size > this.maxFileSize) {
      this.selectedFile = null;
      this.errorMessage = 'CV size must be less than 5MB.';
      return;
    }

    this.selectedFile = file;
  }

  removeFile(event: MouseEvent): void {
    event.stopPropagation();

    this.selectedFile = null;
    this.progress = 0;
    this.isUploading = false;
    this.errorMessage = '';
    this.successMessage = '';

    this.clearProgress();
  }

  submitCv(event: Event): void {
    event.preventDefault();

    this.errorMessage = '';
    this.successMessage = '';

    if (this.isUploading) return;

    if (!this.jobId) {
      this.errorMessage = 'Job is missing. Please choose a job first.';
      return;
    }

    if (!this.selectedFile) {
      this.errorMessage = 'Please upload your CV first.';
      return;
    }

    if (!this.form.name.trim()) {
      this.errorMessage = 'Please enter your full name.';
      return;
    }

    if (!this.form.phone.trim()) {
      this.errorMessage = 'Please enter your phone number.';
      return;
    }

    if (this.form.email.trim() && !this.isValidEmail(this.form.email)) {
      this.errorMessage = 'Please enter a valid email address.';
      return;
    }

    this.isUploading = true;
    this.progress = 0;
    this.clearProgress();

    this.progressInterval = window.setInterval(() => {
      if (this.progress < 90) {
        this.progress += 10;
      }
    }, 180);

    const data = {
      job_id: this.jobId,
      applicant_name: this.form.name.trim(),
      applicant_phone: this.form.phone.trim(),
      applicant_email: this.form.email.trim(),
      cv: this.selectedFile
    };

    this.jobApplicationService.publicApplyToJob(data).subscribe({
      next: (res: any) => {
        this.clearProgress();

        this.progress = 100;
        this.isUploading = false;
        this.errorMessage = '';
        this.successMessage =
          'Application submitted successfully. We will contact you soon.';

        this.form = {
          name: '',
          phone: '',
          email: ''
        };

        this.selectedFile = null;

        setTimeout(() => {
          this.applicationSubmitted.emit(res);
        }, 250);
      },

      error: (err: any) => {
        this.clearProgress();

        this.isUploading = false;
        this.progress = 0;
        this.successMessage = '';

        if (err?.status === 409) {
          this.errorMessage = 'You already applied for this job.';
          return;
        }

        if (err?.error?.message) {
          this.errorMessage = err.error.message;
          return;
        }

        if (err?.error?.errors) {
          const firstKey = Object.keys(err.error.errors)[0];
          this.errorMessage =
            err.error.errors[firstKey]?.[0] || 'Something went wrong.';
          return;
        }

        this.errorMessage = 'Something went wrong. Please try again.';
      }
    });
  }

  formatFileSize(bytes: number): string {
    if (!bytes) {
      return '0 KB';
    }

    const kb = bytes / 1024;

    if (kb < 1024) {
      return `${kb.toFixed(1)} KB`;
    }

    return `${(kb / 1024).toFixed(1)} MB`;
  }

  getFileTypeLabel(file: File | null): string {
    if (!file) {
      return 'CV';
    }

    const name = file.name.toLowerCase();

    if (name.endsWith('.pdf')) {
      return 'PDF';
    }

    if (name.endsWith('.doc')) {
      return 'DOC';
    }

    if (name.endsWith('.docx')) {
      return 'DOCX';
    }

    return 'CV';
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  }

  private clearProgress(): void {
    if (this.progressInterval) {
      window.clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }
}